import { formatCount } from "./usageFormat";

/**
 * How much of a window, or of one row in it, nobody could price.
 *
 * A $0.00 row is not free. Two different failures produce it and every surface
 * that shows a window has to tell them apart: *unpriced* means the tokens are
 * known and the rate is not, *unreported* means the turn ran before the
 * publisher existed and carries no tokens at all. Both counts come from the
 * merge's own fold rather than from `costSource`, because one row can hold
 * each: gpt-6-astra is two priced records and one unpriced.
 *
 * The web Estimated value page and the mobile usage screen both read from
 * here, so the two surfaces cannot drift into saying different things about
 * the same window. `packages/t3-shared` carries the same module for the mobile
 * app, which depends on the `@t3tools` fork rather than on this package.
 *
 * @module usageCoverageCopy
 */

/** The counts a Model or Environment row carries. */
export interface UsageCoverageCounts {
  readonly unpricedRecords: number;
  readonly unreportedRecords: number;
}

function plural(count: number, one: string, many: string): string {
  return `${formatCount(count)} ${count === 1 ? one : many}`;
}

/**
 * The short coverage cell for one row: `1 unpriced`, `14 unreported`, or both.
 *
 * Empty when every record on the row is priced, which is most rows. A boolean
 * label is wrong here: a mixed row would have to pick one of two true things.
 */
export function usageCoverageLabel(row: UsageCoverageCounts): string {
  const parts: string[] = [];
  if (row.unpricedRecords > 0) parts.push(`${formatCount(row.unpricedRecords)} unpriced`);
  if (row.unreportedRecords > 0) parts.push(`${formatCount(row.unreportedRecords)} unreported`);
  return parts.join(" · ");
}

/**
 * The sentences a surface adds under its priced-by line, in plain words.
 *
 * The unreported clause is gated on the count having been reported at all. A
 * server that predates the field measured nothing, and printing `0 turns
 * unreported` there would state a fact nobody established.
 */
export function usageCoverageClauses(
  quality: UsageCoverageCounts & { readonly unreportedRecordsKnown: boolean },
): readonly string[] {
  const clauses: string[] = [];
  if (quality.unpricedRecords > 0) {
    clauses.push(
      `${plural(quality.unpricedRecords, "turn", "turns")} unpriced, tokens known but no rate`,
    );
  }
  if (quality.unreportedRecordsKnown && quality.unreportedRecords > 0) {
    clauses.push(
      `${plural(quality.unreportedRecords, "turn", "turns")} unreported, no tokens recorded`,
    );
  }
  return clauses;
}
